<script setup lang="ts">
  import pluralize from 'pluralize';
  import { computed, onMounted, ref } from 'vue';

  import type { Partner, PartnerInsights } from '@stuut/api/models';
  import { Skeleton } from '@ui/components/skeleton';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import InsightSection from '@/components/partners/InsightSection.vue';

  interface Props {
    partner: Partner;
  }

  const props = defineProps<Props>();
  const insights = ref<PartnerInsights>();
  const isLoading = ref(true);
  const hasError = ref(false);

  const fetchInsights = async () => {
    try {
      isLoading.value = true;
      hasError.value = false;
      insights.value = await stuut.partners.getInsights(props.partner);
    } catch (error: any) {
      hasError.value = true;
      console.error('Failed to fetch partner insights:', error);
      toast.error(error.clientMessage || 'Failed to load partner insights');
    } finally {
      isLoading.value = false;
    }
  };

  const formatDays = (days: number): string => {
    const intDays = Math.floor(days);
    if (intDays === 0) return 'same day';

    return `${intDays} ${pluralize('day', intDays)}`;
  };

  const formatMinutesToDays = (time: number): string => {
    const days = Math.floor(time / (60 * 24));
    return formatDays(days);
  };

  const formatPercentage = (rate: number | null): string => {
    if (rate === null || rate === undefined) return 'N/A';
    return `${Math.round(rate)}%`;
  };

  const formatPeakDay = (dayNum: number | null): string => {
    if (dayNum === null || dayNum === undefined) return 'Unknown';
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNum] || 'Unknown';
  };

  const formatPeakTime = (hour: number): string => {
    const start = hour % 12 || 12;
    const end = (hour + 1) % 12 || 12;
    const period = hour < 12 ? 'AM' : 'PM';
    return `${start}-${end} ${period}`;
  };

  const getPaymentTimingInsightsTitle = computed(() => {
    if (!insights.value?.payment_timing) return '';

    return `Usually pays invoices in <span class='font-semibold'>${formatDays(insights.value.payment_timing.average_days_to_pay)}</span>.`;
  });

  const getPaymentTimingInsightsSubtitle = computed(() => {
    if (!insights.value?.payment_timing) return null;

    const { days_past_due_avg: daysPastDueAvg, on_time_payment_rate: onTimePaymentRate } =
      insights.value.payment_timing;

    const latePercentage = 100 - onTimePaymentRate;

    if (latePercentage < 10) {
      return `Excellent payment timing, typically pays <span class="font-semibold">${formatPercentage(onTimePaymentRate)}</span> on time`;
    } else if (latePercentage < 25) {
      return `Good payment timing, typically pays <span class="font-semibold">${formatPercentage(onTimePaymentRate)}</span> on time`;
    }

    const lateDays = daysPastDueAvg > 0 ? formatDays(daysPastDueAvg) : 'several days';
    return `Pays late by <span class="font-semibold">${lateDays} ${formatPercentage(latePercentage)}</span> of the time`;
  });

  const getEmailResponsivenessInsightsTitle = computed(() => {
    if (!insights.value?.email_responsiveness) return '';

    return `Responds to emails in <span class='font-semibold'>${formatMinutesToDays(insights.value.email_responsiveness.average_response_time)}</span>.`;
  });

  const getEmailResponsivenessInsightsSubtitle = computed(() => {
    if (!insights.value?.email_responsiveness) return null;

    const { peak_response_day: peakResponseDay, peak_response_time: peakResponseTime } =
      insights.value.email_responsiveness;
    return `Their peak response time is <span class="font-semibold">${formatPeakDay(peakResponseDay)}, ${formatPeakTime(peakResponseTime)}</span>.`;
  });

  const getCallResponsivenessInsightsTitle = computed(() => {
    if (!insights.value?.call_responsiveness) return '';

    return `Picks up calls <span class="font-semibold">${formatPercentage(insights.value.call_responsiveness.connection_rate)} of the time</span>.`;
  });

  const getCallResponsivenessInsightsSubtitle = computed(() => {
    if (!insights.value?.call_responsiveness) return '';

    const status =
      insights.value.call_responsiveness.connection_rate >= 30 ? 'Responsive' : 'Unresponsive';
    return `<span class="font-semibold">${status}</span> to agent calling.`;
  });

  const getCommunicationPreferencesInsightsTitle = computed(() => {
    if (!insights.value?.communication_preferences) return '';

    const { average_followups_needed: averageFollowupsNeeded } =
      insights.value.communication_preferences;

    if (averageFollowupsNeeded === 0) {
      return `Usually responds  <span class="font-semibold">immediately</span> without followups.`;
    }

    return `Usually responds after <span class="font-semibold">${averageFollowupsNeeded} ${pluralize('followup', averageFollowupsNeeded)}</span>.`;
  });

  const getCommunicationPreferencesInsightsSubtitle = computed(() => {
    if (!insights.value?.communication_preferences) return '';

    return `<span class="font-semibold">${insights.value.communication_preferences.most_responsive_contact}</span> is the most responsive contact.`;
  });

  onMounted(() => {
    fetchInsights();
  });
</script>

<template>
  <div v-if="!hasError" class="flex flex-col gap-4">
    <div v-if="isLoading" class="flex flex-col gap-4">
      <Skeleton class="h-4 w-[50%]" />
      <Skeleton class="h-4 w-[70%]" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <template v-if="insights && insights.hasAnyInsights">
        <InsightSection
          v-if="insights.payment_timing"
          icon="fa-dollar"
          :title="getPaymentTimingInsightsTitle"
          :subtitle="getPaymentTimingInsightsSubtitle"
        />

        <InsightSection
          v-if="insights.email_responsiveness"
          icon="fa-envelope"
          :title="getEmailResponsivenessInsightsTitle"
          :subtitle="getEmailResponsivenessInsightsSubtitle"
        />

        <InsightSection
          v-if="insights.call_responsiveness"
          icon="fa-phone"
          :title="getCallResponsivenessInsightsTitle"
          :subtitle="getCallResponsivenessInsightsSubtitle"
        />

        <InsightSection
          v-if="insights.communication_preferences"
          icon="fa-users"
          :title="getCommunicationPreferencesInsightsTitle"
          :subtitle="getCommunicationPreferencesInsightsSubtitle"
        />
      </template>

      <template v-else>
        <span class="flex items-center gap-3 text-xs text-muted-foreground">
          <i class="text-2xs fas fa-sparkle" />
          <span>No insights available for this customer at the moment.</span>
        </span>
      </template>
    </div>
  </div>
</template>

<style scoped></style>
